import React, { useEffect, useState } from "react";
import api, { API_BASE, fileUrl, getToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, MoreHorizontal, Search, Trash2, Eye, ArrowRightLeft, Printer,
  UploadCloud, Camera, Paperclip, Download,
} from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";

const MARITAL = ["single", "married", "divorced", "widowed"];
const ATTACHMENT_CATEGORIES = [
  "id_copy", "academic_certificate", "contract", "recommendation_letter",
  "photo", "payslip", "appraisal", "other",
];

const blankPerson = { name: "", nin: "", contact: "", place_of_residence: "" };
const BLANK = {
  staff_number: "", surname: "", given_name: "", other_names: "",
  date_of_birth: "", gender: "", nin: "", contact: "", email: "",
  title: "", department: "", branch: "", date_joined: "",
  marital_status: "single", spouse_name: "", spouse_contact: "",
  father: { ...blankPerson },
  mother: { ...blankPerson },
  recommender: { ...blankPerson, relationship: "" },
  bank: { bank_name: "", branch_name: "", account_name: "", account_number: "" },
  photo_file_id: "", notes: "", is_active: true,
};

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { id, filename, content_type, size }
}

export default function Staff() {
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openNew, setOpenNew] = useState(false);
  const [openView, setOpenView] = useState(null); // staff object
  const [openTransfer, setOpenTransfer] = useState(null);
  const [q, setQ] = useState("");
  const [filterDept, setFilterDept] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: s }, { data: d }, { data: b }] = await Promise.all([
          api.get("/staff"), api.get("/departments"), api.get("/branches"),
        ]);
        if (!cancelled) { setItems(s); setDepartments(d); setBranches(b); }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const reload = () => setRefreshKey((k) => k + 1);

  const remove = async (id) => {
    if (!window.confirm("Delete this staff record permanently?")) return;
    try {
      await api.delete(`/staff/${id}`);
      toast.success("Staff record deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadReport = async (s) => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/staff/${s.id}/report.pdf`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      toast.error("Could not generate report");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `staff-${s.staff_number || s.id}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const filtered = items.filter((s) => {
    if (filterDept !== "all" && s.department !== filterDept) return false;
    if (q) {
      const t = `${s.staff_number} ${s.surname} ${s.given_name} ${s.other_names} ${s.contact} ${s.email} ${s.nin} ${s.department} ${s.title}`.toLowerCase();
      if (!t.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div>
      <PageHeader
        eyebrow="Administration · Personnel"
        title="Staff Bio Data"
        subtitle="Personnel registry — identity, next of kin, banking, recommenders, attachments and printable profile reports."
        actions={
          <StaffFormDialog
            open={openNew} setOpen={setOpenNew}
            departments={departments} branches={branches}
            onSaved={reload}
          />
        }
      />

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search by name, staff #, NIN, contact…" value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-8 h-9"
            data-testid="staff-search-input" />
        </div>
        <Select value={filterDept} onValueChange={setFilterDept}>
          <SelectTrigger className="w-56 h-9" data-testid="staff-filter-dept">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-gray-500">{filtered.length} staff</span>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No staff records yet" hint="Click “Add staff” to register your first staff member." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="staff-table">
              <thead className="bg-gray-50">
                <tr>
                  {["", "Staff #", "Name", "Title", "Department", "Branch", "Contact", "Status", ""].map((h, i) => (
                    <th key={i} className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <Avatar staff={s} size={36} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{s.staff_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{s.surname} {s.given_name}</div>
                      <div className="text-xs text-gray-500">{s.other_names || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{s.title || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{s.department || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{s.branch || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{s.contact || "—"}</td>
                    <td className="px-4 py-3">
                      <Badge tone={s.is_active === false ? "gray" : "emerald"}>
                        {s.is_active === false ? "Inactive" : "Active"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`staff-actions-${s.id}`}>
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setOpenView(s)} data-testid={`staff-view-${s.id}`}>
                            <Eye size={13} className="mr-2" /> View profile
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadReport(s)} data-testid={`staff-print-${s.id}`}>
                            <Printer size={13} className="mr-2" /> Print PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setOpenTransfer(s)}>
                            <ArrowRightLeft size={13} className="mr-2" /> Transfer dept.
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => remove(s.id)} className="text-red-700">
                            <Trash2 size={13} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View profile dialog */}
      {openView && (
        <StaffViewDialog
          staff={openView}
          onClose={() => setOpenView(null)}
          onUpdated={(updated) => { setOpenView(updated); reload(); }}
          onPrint={() => downloadReport(openView)}
        />
      )}

      {/* Transfer dialog */}
      {openTransfer && (
        <TransferDialog
          staff={openTransfer}
          departments={departments}
          branches={branches}
          onClose={() => setOpenTransfer(null)}
          onDone={() => { setOpenTransfer(null); reload(); }}
        />
      )}
    </div>
  );
}

function Avatar({ staff, size = 40 }) {
  const initials = `${(staff.surname || "?").charAt(0)}${(staff.given_name || "").charAt(0)}`.toUpperCase();
  if (staff.photo_file_id) {
    return (
      <img
        src={fileUrl(staff.photo_file_id)}
        alt={initials}
        className="rounded-full object-cover bg-emerald-100"
        style={{ height: size, width: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-emerald-900 text-white flex items-center justify-center font-semibold text-xs"
      style={{ height: size, width: size }}
    >
      {initials}
    </div>
  );
}

function StaffFormDialog({ open, setOpen, departments, branches, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const photoPreview = photoFile ? URL.createObjectURL(photoFile) : (form.photo_file_id ? fileUrl(form.photo_file_id) : "");

  const setField = (path, value) => {
    setForm((f) => {
      const next = { ...f };
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let payload = { ...form };
      if (photoFile) {
        const up = await uploadFile(photoFile);
        payload.photo_file_id = up.id;
      }
      // strip empty optional sub-objects? Keep them — backend tolerates nulls.
      await api.post("/staff", payload);
      toast.success("Staff registered");
      setOpen(false);
      setForm(BLANK);
      setPhotoFile(null);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(BLANK); setPhotoFile(null); } }}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="staff-new-button">
          <Plus size={16} className="mr-1.5" /> Add staff
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Add staff member</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="staff-form">
          <Tabs defaultValue="identity">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="identity">Identity</TabsTrigger>
              <TabsTrigger value="family">Family</TabsTrigger>
              <TabsTrigger value="parents">Parents</TabsTrigger>
              <TabsTrigger value="recommender">Recommender</TabsTrigger>
              <TabsTrigger value="bank">Bank</TabsTrigger>
            </TabsList>

            {/* Identity */}
            <TabsContent value="identity" className="space-y-3 pt-3">
              <div className="grid grid-cols-1 md:grid-cols-[140px,1fr] gap-4">
                <div className="text-center">
                  {photoPreview ? (
                    <img src={photoPreview} alt="" className="h-32 w-32 rounded-md object-cover border border-gray-200" />
                  ) : (
                    <div className="h-32 w-32 rounded-md bg-emerald-50 border border-dashed border-emerald-300 flex items-center justify-center text-emerald-700">
                      <Camera size={24} />
                    </div>
                  )}
                  <label className="mt-2 inline-flex items-center gap-1 text-xs cursor-pointer text-emerald-900 hover:underline">
                    <UploadCloud size={12} />
                    <span>Upload photo</span>
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                      data-testid="staff-photo-input" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Staff #" required testid="staff-number-input"
                    value={form.staff_number} onChange={(v) => setField("staff_number", v)} />
                  <Field label="Title / Position" testid="staff-title-input"
                    value={form.title} onChange={(v) => setField("title", v)} />
                  <Field label="Surname" required testid="staff-surname-input"
                    value={form.surname} onChange={(v) => setField("surname", v)} />
                  <Field label="Given name" required testid="staff-given-input"
                    value={form.given_name} onChange={(v) => setField("given_name", v)} />
                  <Field label="Other names" testid="staff-other-input"
                    value={form.other_names} onChange={(v) => setField("other_names", v)} />
                  <Field label="Date of birth" type="date"
                    value={form.date_of_birth} onChange={(v) => setField("date_of_birth", v)} />
                  <SelectField label="Gender" value={form.gender || ""}
                    options={["male", "female", "other"]}
                    onChange={(v) => setField("gender", v)} />
                  <Field label="NIN" value={form.nin} onChange={(v) => setField("nin", v)} />
                  <Field label="Contact phone" value={form.contact} onChange={(v) => setField("contact", v)} />
                  <Field label="Email" value={form.email} onChange={(v) => setField("email", v)} />
                  <SelectField label="Department" value={form.department || ""}
                    options={departments.map((d) => d.name)}
                    onChange={(v) => setField("department", v)}
                    testid="staff-department-select" />
                  <SelectField label="Branch" value={form.branch || ""}
                    options={branches.map((b) => b.name)}
                    onChange={(v) => setField("branch", v)} />
                  <Field label="Date joined" type="date"
                    value={form.date_joined} onChange={(v) => setField("date_joined", v)} />
                </div>
              </div>
            </TabsContent>

            {/* Family / Marital */}
            <TabsContent value="family" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Marital status" value={form.marital_status}
                  options={MARITAL}
                  onChange={(v) => setField("marital_status", v)}
                  testid="staff-marital-select" />
              </div>
              {form.marital_status === "married" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Spouse name" value={form.spouse_name}
                    onChange={(v) => setField("spouse_name", v)} testid="staff-spouse-name-input" />
                  <Field label="Spouse contact" value={form.spouse_contact}
                    onChange={(v) => setField("spouse_contact", v)} />
                </div>
              )}
            </TabsContent>

            {/* Parents */}
            <TabsContent value="parents" className="space-y-4 pt-3">
              <PersonBlock title="Father" prefix="father" form={form} setField={setField} />
              <PersonBlock title="Mother" prefix="mother" form={form} setField={setField} />
            </TabsContent>

            {/* Recommender */}
            <TabsContent value="recommender" className="space-y-3 pt-3">
              <PersonBlock title="Recommender / Reference" prefix="recommender"
                form={form} setField={setField} withRelationship />
            </TabsContent>

            {/* Bank */}
            <TabsContent value="bank" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Bank name" value={form.bank.bank_name}
                  onChange={(v) => setField("bank.bank_name", v)} testid="staff-bank-name-input" />
                <Field label="Branch" value={form.bank.branch_name}
                  onChange={(v) => setField("bank.branch_name", v)} />
                <Field label="Account name" value={form.bank.account_name}
                  onChange={(v) => setField("bank.account_name", v)} />
                <Field label="Account number" value={form.bank.account_number}
                  onChange={(v) => setField("bank.account_number", v)} testid="staff-bank-acct-input" />
              </div>
            </TabsContent>
          </Tabs>

          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setField("notes", e.target.value)} className="mt-1" />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={busy}
              className="bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="staff-submit-button">
              {busy ? "Saving…" : "Save staff record"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-red-600 ml-1">*</span>}</Label>
      <Input type={type} required={required} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1" data-testid={testid} />
    </div>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}>
          <SelectValue placeholder={`Choose ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{(o || "").replaceAll("_", " ")}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PersonBlock({ title, prefix, form, setField, withRelationship }) {
  const p = form[prefix] || {};
  return (
    <div className="border border-gray-200 rounded-md p-3">
      <div className="font-semibold text-emerald-900 mb-2 text-sm">{title}</div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" value={p.name} onChange={(v) => setField(`${prefix}.name`, v)} />
        <Field label="NIN" value={p.nin} onChange={(v) => setField(`${prefix}.nin`, v)} />
        <Field label="Contact" value={p.contact} onChange={(v) => setField(`${prefix}.contact`, v)} />
        <Field label="Place of residence" value={p.place_of_residence}
          onChange={(v) => setField(`${prefix}.place_of_residence`, v)} />
        {withRelationship && (
          <Field label="Relationship" value={p.relationship}
            onChange={(v) => setField(`${prefix}.relationship`, v)} />
        )}
      </div>
    </div>
  );
}

function StaffViewDialog({ staff, onClose, onUpdated, onPrint }) {
  const [attachments, setAttachments] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [attachKey, setAttachKey] = useState(0);
  const [uploadCat, setUploadCat] = useState("id_copy");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploading, setUploading] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: a }, { data: t }] = await Promise.all([
        api.get(`/staff/${staff.id}/attachments`),
        api.get(`/staff/${staff.id}/transfers`),
      ]);
      if (!cancelled) { setAttachments(a); setTransfers(t); }
    })();
    return () => { cancelled = true; };
  }, [staff.id, attachKey]);

  const handleAttachUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const up = await uploadFile(file);
      await api.post(`/staff/${staff.id}/attachments`, {
        file_id: up.id,
        category: uploadCat,
        description: uploadDesc,
        original_filename: up.filename,
      });
      toast.success("Attachment uploaded");
      setUploadDesc("");
      setAttachKey((k) => k + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm("Delete this attachment?")) return;
    await api.delete(`/staff/${staff.id}/attachments/${att.id}`);
    setAttachKey((k) => k + 1);
  };

  const replacePhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const up = await uploadFile(file);
      const { data } = await api.patch(`/staff/${staff.id}`, { photo_file_id: up.id });
      onUpdated(data);
      toast.success("Photo updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPhotoBusy(false); }
  };

  const fullName = `${staff.surname || ""} ${staff.given_name || ""} ${staff.other_names || ""}`.trim();

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between">
            <span>{fullName}</span>
            <div className="flex gap-2">
              <Button size="sm" onClick={onPrint}
                className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="staff-view-print-button">
                <Printer size={14} className="mr-1.5" /> Print PDF
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Top: photo + identity */}
        <div className="grid grid-cols-1 md:grid-cols-[160px,1fr] gap-5">
          <div className="text-center">
            <Avatar staff={staff} size={144} />
            <label className="mt-2 inline-flex items-center gap-1 text-xs cursor-pointer text-emerald-900 hover:underline">
              <UploadCloud size={12} />
              <span>{photoBusy ? "Uploading…" : "Replace photo"}</span>
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => replacePhoto(e.target.files?.[0])} />
            </label>
          </div>
          <div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <KV label="Staff #" value={staff.staff_number} />
              <KV label="Title" value={staff.title} />
              <KV label="Department" value={staff.department} />
              <KV label="Branch" value={staff.branch} />
              <KV label="Date of birth" value={staff.date_of_birth} />
              <KV label="Gender" value={staff.gender} />
              <KV label="NIN" value={staff.nin} />
              <KV label="Contact" value={staff.contact} />
              <KV label="Email" value={staff.email} />
              <KV label="Date joined" value={staff.date_joined} />
              <KV label="Marital status" value={staff.marital_status} />
              <KV label="Status" value={staff.is_active === false ? "Inactive" : "Active"} />
            </div>
          </div>
        </div>

        {/* Sections */}
        {staff.marital_status === "married" && (
          <Section title="Spouse">
            <KV label="Name" value={staff.spouse_name} />
            <KV label="Contact" value={staff.spouse_contact} />
          </Section>
        )}
        <Section title="Father">
          <PersonKV person={staff.father} />
        </Section>
        <Section title="Mother">
          <PersonKV person={staff.mother} />
        </Section>
        <Section title="Recommender / Reference">
          <PersonKV person={staff.recommender} withRelationship />
        </Section>
        <Section title="Bank details">
          <KV label="Bank name" value={staff.bank?.bank_name} />
          <KV label="Branch" value={staff.bank?.branch_name} />
          <KV label="Account name" value={staff.bank?.account_name} />
          <KV label="Account number" value={staff.bank?.account_number} />
        </Section>

        {/* Transfer history */}
        <div className="mt-2">
          <h3 className="font-display font-semibold text-gray-900 mb-2">Transfer history</h3>
          {transfers.length === 0 ? (
            <div className="text-xs text-gray-500">No transfers recorded.</div>
          ) : (
            <ul className="text-sm space-y-1.5">
              {transfers.map((t) => (
                <li key={t.id} className="flex items-center gap-2">
                  <Badge tone="blue">{(t.effective_date || t.created_at || "").slice(0, 10)}</Badge>
                  <span className="text-gray-700">{t.from_department || "—"} → <strong>{t.to_department}</strong></span>
                  {t.reason && <span className="text-xs text-gray-500">· {t.reason}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Attachments */}
        <div className="mt-2">
          <h3 className="font-display font-semibold text-gray-900 mb-2">Attachments</h3>
          <div className="bg-amber-50/40 border border-dashed border-amber-300 rounded-md p-3 mb-3 flex flex-col sm:flex-row gap-2 items-end">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-[180px,1fr] gap-2">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={uploadCat} onValueChange={setUploadCat}>
                  <SelectTrigger className="h-9 mt-1" data-testid="staff-attach-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ATTACHMENT_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Input value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)}
                  className="h-9 mt-1" placeholder="e.g. Passport copy front" />
              </div>
            </div>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input type="file" className="hidden"
                onChange={(e) => handleAttachUpload(e.target.files?.[0])}
                data-testid="staff-attach-file-input" />
              <Button type="button" disabled={uploading} asChild
                className="bg-amber-600 hover:bg-amber-700 text-white">
                <span><UploadCloud size={14} className="mr-1.5" />
                  {uploading ? "Uploading…" : "Upload"}</span>
              </Button>
            </label>
          </div>
          {attachments.length === 0 ? (
            <div className="text-xs text-gray-500">No attachments yet.</div>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
              {attachments.map((a) => (
                <li key={a.id} className="flex items-center gap-3 px-3 py-2">
                  <Paperclip size={14} className="text-emerald-700 flex-shrink-0" />
                  <Badge tone="emerald">{(a.category || "").replaceAll("_", " ")}</Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{a.original_filename || a.file_id}</div>
                    {a.description && <div className="text-xs text-gray-500 truncate">{a.description}</div>}
                  </div>
                  <a href={fileUrl(a.file_id)} target="_blank" rel="noreferrer"
                    className="text-xs inline-flex items-center gap-1 text-emerald-900 hover:underline">
                    <Download size={11} /> Download
                  </a>
                  <Button variant="ghost" size="sm" onClick={() => deleteAttachment(a)}
                    className="text-red-700 hover:bg-red-50">
                    <Trash2 size={13} />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {staff.notes && (
          <div className="mt-2">
            <h3 className="font-display font-semibold text-gray-900 mb-1">Notes</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{staff.notes}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <h3 className="font-display font-semibold text-gray-900 mb-2">{title}</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">{children}</div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
      <div className="text-sm text-gray-900">{value || "—"}</div>
    </div>
  );
}

function PersonKV({ person, withRelationship }) {
  const p = person || {};
  return (
    <>
      <KV label="Name" value={p.name} />
      <KV label="NIN" value={p.nin} />
      <KV label="Contact" value={p.contact} />
      <KV label="Place of residence" value={p.place_of_residence} />
      {withRelationship && <KV label="Relationship" value={p.relationship} />}
    </>
  );
}

function TransferDialog({ staff, departments, branches, onClose, onDone }) {
  const [toDept, setToDept] = useState("");
  const [toBranch, setToBranch] = useState(staff.branch || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!toDept) return;
    setBusy(true);
    try {
      await api.post(`/staff/${staff.id}/transfer`, {
        to_department: toDept, to_branch: toBranch, reason,
        effective_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Staff transferred");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Inter-department transfer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="staff-transfer-form">
          <div className="bg-gray-50 rounded-md p-3 text-sm">
            <div className="font-medium text-gray-900">{staff.surname} {staff.given_name}</div>
            <div className="text-xs text-gray-600 mt-1">
              Current: <strong>{staff.department || "—"}</strong>
              {staff.branch && <span> · {staff.branch}</span>}
            </div>
          </div>
          <SelectField label="Transfer to department" value={toDept}
            options={departments.filter((d) => d.name !== staff.department).map((d) => d.name)}
            onChange={setToDept}
            testid="staff-transfer-dept-select" />
          <SelectField label="Branch (optional)" value={toBranch}
            options={branches.map((b) => b.name)} onChange={setToBranch} />
          <div>
            <Label>Reason</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" />
          </div>
          <Button type="submit" disabled={busy || !toDept}
            className="w-full bg-emerald-900 hover:bg-emerald-950 text-white"
            data-testid="staff-transfer-submit-button">
            {busy ? "Transferring…" : "Confirm transfer"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
